import React, { useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ColumnItem } from './ColumnItem';
import type { Column } from './helpers';

interface SortableColumnItemProps {
  column: Column;
  /** 1-based position within the category group */
  columnIndex: number;
  onCancel: () => void;
  onSave: (columnData: Partial<Column>) => void;
  totalColumns: number;
  isDragDisabled?: boolean;
  isLocked?: boolean;
  onDelete?: () => void;
}

export function SortableColumnItem(props: SortableColumnItemProps): JSX.Element {
  const [isLocallyEditing, setIsLocallyEditing] = useState(false);
  const dragDisabled = isLocallyEditing || !!props.isDragDisabled;
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled: dragDisabled,
    id: props.column.id,
  });

  // Placeholder styling when item is being dragged (ghost is shown via DragOverlay)
  const style: React.CSSProperties = {
    backgroundColor: isDragging ? '#F5F5F5' : undefined,
    border: isDragging ? '2px dashed #BDBDBD' : undefined,
    borderRadius: isDragging ? 8 : undefined,
    boxSizing: 'border-box',
    cursor: dragDisabled ? 'default' : isDragging ? 'grabbing' : 'grab',
    opacity: isDragging ? 0.4 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };
  /**
   * Listeners go on the root element so the whole row is a drag target; the handle is
   * visual only. A disabled row takes neither, so it isn't announced as sortable.
   */
  const dragProps = !dragDisabled ? { ...attributes, ...listeners } : {};

  return (
    <div ref={setNodeRef} data-dragging={isDragging ? '' : undefined} style={style} {...dragProps}>
      <ColumnItem
        {...props}
        dragHandleProps={{}}
        isDragDisabled={dragDisabled}
        onEditingChange={setIsLocallyEditing}
      />
    </div>
  );
}
